import { __read } from "tslib";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrandedRevealImage } from './BrandedRevealImage';
/**
 * Thumbnail navigation below overlay main image (B2B + salgsbog).
 * Carousel layout: single row with horizontal scroll + prev/next controls.
 */
export var ProductGalleryThumbStrip = function (_a) {
    var thumbs = _a.thumbs, groups = _a.groups, currentIndex = _a.currentIndex, onSelect = _a.onSelect, onThumbLoad = _a.onThumbLoad, onThumbError = _a.onThumbError, stripRef = _a.stripRef, _b = _a.layout, layout = _b === void 0 ? 'grid' : _b;
    var scrollRef = useRef(null);
    var didInitialScrollRef = useRef(false);
    var _c = __read(useState(false), 2), canScrollPrev = _c[0], setCanScrollPrev = _c[1];
    var _d = __read(useState(false), 2), canScrollNext = _d[0], setCanScrollNext = _d[1];
    var flatThumbs = groups
        ? groups.reduce(function (acc, group) { return acc.concat(group); }, [])
        : thumbs !== null && thumbs !== void 0 ? thumbs : [];
    var total = flatThumbs.length;
    var isCarousel = layout === 'carousel';
    var setCombinedRef = useCallback(function (node) {
        scrollRef.current = node;
        if (typeof stripRef === 'function') {
            stripRef(node);
        }
        else if (stripRef && 'current' in stripRef) {
            stripRef.current = node;
        }
    }, [stripRef]);
    var updateScrollState = useCallback(function () {
        var el = scrollRef.current;
        if (!el || !isCarousel) {
            setCanScrollPrev(false);
            setCanScrollNext(false);
            return;
        }
        var maxScroll = el.scrollWidth - el.clientWidth;
        setCanScrollPrev(el.scrollLeft > 2);
        setCanScrollNext(el.scrollLeft < maxScroll - 2);
    }, [isCarousel]);
    var scrollActiveIntoView = useCallback(function (behavior) {
        if (behavior === void 0) { behavior = 'smooth'; }
        var el = scrollRef.current;
        if (!el || !isCarousel) {
            return;
        }
        var active = el.querySelector("[data-thumb-index=\"".concat(currentIndex, "\"]"));
        if (!active) {
            return;
        }
        var relativeLeft = active.getBoundingClientRect().left -
            el.getBoundingClientRect().left +
            el.scrollLeft;
        var targetLeft = relativeLeft - (el.clientWidth - active.clientWidth) / 2;
        var maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
        el.scrollTo({
            left: Math.min(maxScroll, Math.max(0, targetLeft)),
            behavior: behavior,
        });
        updateScrollState();
    }, [currentIndex, isCarousel, updateScrollState]);
    useEffect(function () {
        if (total < 1) {
            didInitialScrollRef.current = false;
        }
    }, [total]);
    useEffect(function () {
        updateScrollState();
    }, [flatThumbs, updateScrollState]);
    useEffect(function () {
        var el = scrollRef.current;
        if (!el || !isCarousel) {
            return;
        }
        var onScroll = function () { return updateScrollState(); };
        el.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        return function () {
            el.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, [isCarousel, updateScrollState]);
    useEffect(function () {
        if (!isCarousel || total < 1) {
            return;
        }
        var behavior = didInitialScrollRef.current
            ? 'smooth'
            : 'auto';
        var run = function () { return scrollActiveIntoView(behavior); };
        requestAnimationFrame(function () {
            requestAnimationFrame(run);
        });
        didInitialScrollRef.current = true;
    }, [currentIndex, isCarousel, total, scrollActiveIntoView]);
    if (total < 1) {
        return null;
    }
    if (!isCarousel && total < 2) {
        return null;
    }
    var scrollBy = function (direction) {
        var el = scrollRef.current;
        if (!el) {
            return;
        }
        el.scrollBy({ left: direction * Math.max(120, el.clientWidth * 0.6), behavior: 'smooth' });
    };
    var renderThumbButton = function (thumb, index) {
        var isActive = index === currentIndex;
        return (React.createElement("div", { key: index, role: "presentation", "data-thumb-index": index, className: 'po__left__gallery-thumb' +
                (isActive ? ' po__left__gallery-thumb--active' : ''), onMouseDown: function (e) { return e.preventDefault(); }, onClick: function () { return onSelect(index); } },
            React.createElement(BrandedRevealImage, { variant: "overlay-thumb", src: thumb.src, alt: "", dataThumbIndex: index, imageClassName: "po__left__gallery-thumb__img", onLoad: onThumbLoad
                    ? function () {
                        onThumbLoad(index);
                        if (index === currentIndex) {
                            requestAnimationFrame(function () {
                                scrollActiveIntoView('auto');
                            });
                        }
                    }
                    : index === currentIndex
                        ? function () {
                            requestAnimationFrame(function () {
                                scrollActiveIntoView('auto');
                            });
                        }
                        : undefined, onError: onThumbError
                    ? function () {
                        onThumbError(index);
                        if (index === currentIndex) {
                            requestAnimationFrame(function () {
                                scrollActiveIntoView('auto');
                            });
                        }
                    }
                    : undefined })));
    };
    var renderStripContent = function () {
        if (groups && groups.length > 0) {
            var flatIndex_1 = 0;
            return groups.map(function (group, groupIndex) { return (React.createElement(React.Fragment, { key: groupIndex },
                groupIndex > 0 && (React.createElement("span", { className: "po__left__gallery-thumb-separator", "aria-hidden": "true" })),
                group.map(function (thumb) {
                    var index = flatIndex_1;
                    flatIndex_1 += 1;
                    return renderThumbButton(thumb, index);
                }))); });
        }
        return (thumbs !== null && thumbs !== void 0 ? thumbs : []).map(function (thumb, index) { return (React.createElement(React.Fragment, { key: index },
            thumb.separatorBefore && (React.createElement("span", { className: "po__left__gallery-thumb-separator", "aria-hidden": "true" })),
            renderThumbButton(thumb, index))); });
    };
    var strip = (React.createElement("div", { className: 'po__left__thumbnails' + (isCarousel ? ' po__left__thumbnails--carousel' : ''), ref: isCarousel ? setCombinedRef : stripRef, role: "group", "aria-label": "Product images" }, renderStripContent()));
    if (!isCarousel) {
        return strip;
    }
    return (React.createElement("div", { className: "po__left__thumbnails-carousel" },
        React.createElement("div", { role: "presentation", className: 'po__left__thumbnails-carousel__nav po__left__thumbnails-carousel__nav--prev' +
                (!canScrollPrev ? ' po__left__thumbnails-carousel__nav--disabled' : ''), "aria-hidden": "true", onMouseDown: function (e) { return e.preventDefault(); }, onClick: function () { return canScrollPrev && scrollBy(-1); } }, "\u2039"),
        strip,
        React.createElement("div", { role: "presentation", className: 'po__left__thumbnails-carousel__nav po__left__thumbnails-carousel__nav--next' +
                (!canScrollNext ? ' po__left__thumbnails-carousel__nav--disabled' : ''), "aria-hidden": "true", onMouseDown: function (e) { return e.preventDefault(); }, onClick: function () { return canScrollNext && scrollBy(1); } }, "\u203A")));
};
export default ProductGalleryThumbStrip;
//# sourceMappingURL=ProductGalleryThumbStrip.js.map